import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Phone, MapPin, Navigation, PackageCheck, CheckCircle2, XCircle, ArrowLeft,
  IndianRupee, User2, Loader2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const FAILURE_REASONS = [
  "Customer unavailable",
  "Wrong address",
  "Customer refused delivery",
  "Damaged items",
  "COD payment refused",
  "Other",
];

export default function DeliveryOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [failOpen, setFailOpen] = useState(false);
  const [failReason, setFailReason] = useState(FAILURE_REASONS[0]);
  const [failNote, setFailNote] = useState("");

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patchDelivery = async (delivery_status, payload = {}) => {
    setBusy(true);
    try {
      await api.patch(`/orders/${id}/delivery-status`, { delivery_status, ...payload });
      toast.success(`Status → ${delivery_status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const confirmPickup = () => patchDelivery("picked_up");
  const startTransit = () => patchDelivery("in_transit");
  const submitDeliver = async () => {
    if (!otp || otp.length < 4) { toast.error("Enter the 4–6 digit OTP shown to the customer"); return; }
    try {
      await api.patch(`/orders/${id}/delivery-status`, { delivery_status: "delivered", otp });
      toast.success("Delivery complete 🎉");
      setOtpOpen(false); setOtp("");
      load();
    } catch (e) { toast.error(errMsg(e, "OTP mismatch or update failed")); }
  };
  const submitFail = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${id}/delivery-status`, {
        delivery_status: "failed",
        otp: `[reason] ${failReason}${failNote ? ` — ${failNote}` : ""}`.slice(0, 120),
      });
      toast.success("Marked as failed delivery");
      setFailOpen(false); setFailReason(FAILURE_REASONS[0]); setFailNote("");
      nav("/delivery");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!order) return <div className="p-4 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;

  const addr = order.address || {};
  const deliveryState = order.delivery?.status || "";
  const partnerPicked = ["picked_up", "in_transit"].includes(deliveryState);
  const isTransit = order.status === "out_for_delivery" || deliveryState === "in_transit";
  const isDelivered = order.status === "delivered";
  const isCancelled = ["cancelled", "returned"].includes(order.status);

  const dialAddr = encodeURIComponent(
    [addr.line1, addr.line2, addr.landmark, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")
  );
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dialAddr}`;
  const telUrl = addr.phone ? `tel:${addr.phone.replace(/[^0-9+]/g, "")}` : null;

  return (
    <div className="p-4 space-y-4" data-testid="delivery-order-detail">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-runs">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Header */}
      <Card className="card-soft border-transparent" data-testid="order-header-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Order</div>
              <div className="font-mono font-semibold text-lg">{order.order_number}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Assigned {fmtDate(order.delivery?.assigned_at || order.updated_at)}</div>
            </div>
            <Badge variant="outline" className="capitalize">{order.status?.replace(/_/g, " ")}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Customer + address */}
      <Card className="card-soft border-transparent" data-testid="customer-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-primary text-white grid place-items-center shrink-0">
              <User2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{addr.name || "—"}</div>
              <div className="text-sm text-muted-foreground">{addr.phone || "no phone"}</div>
              <div className="text-sm text-muted-foreground mt-2 flex items-start gap-1">
                <MapPin className="w-4 h-4 mt-0.5 text-accent shrink-0" />
                <div>
                  <div>{[addr.line1, addr.line2, addr.landmark].filter(Boolean).join(", ")}</div>
                  <div>{addr.city}, {addr.state} — {addr.pincode}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button asChild variant="outline" disabled={!telUrl} data-testid="call-customer-btn">
              <a href={telUrl || "#"}><Phone className="w-4 h-4 mr-2" />Call</a>
            </Button>
            <Button asChild className="btn-primary-glow" data-testid="navigate-btn">
              <a href={mapsUrl} target="_blank" rel="noreferrer"><Navigation className="w-4 h-4 mr-2" />Navigate</a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="card-soft border-transparent" data-testid="items-card">
        <CardContent className="p-0">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="font-display text-base">Package ({order.items?.length})</div>
            <Badge variant="secondary" className="text-xs">{(order.items || []).reduce((s, x) => s + x.qty, 0)} units</Badge>
          </div>
          <div className="divide-y">
            {(order.items || []).map((it, i) => (
              <div key={i} className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center shrink-0 text-xs font-mono">×{it.qty}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-xs text-muted-foreground">{it.sku || "—"}</div>
                </div>
                <div className="text-sm font-semibold">{money(Number(it.price_snapshot) * Number(it.qty))}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment */}
      <Card className="card-soft border-transparent" data-testid="payment-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl grid place-items-center ${order.payment?.status === "captured" ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}>
            <IndianRupee className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm">Payment · <span className="uppercase font-medium">{order.payment?.method}</span></div>
            <div className="text-xs text-muted-foreground">Status: {order.payment?.status}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-muted-foreground">To collect</div>
            <div className="font-display font-semibold text-lg">
              {order.payment?.method === "cod" && order.payment?.status !== "captured" ? money(order.pricing?.total) : "—"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      {!isDelivered && !isCancelled && (
        <div className="space-y-2" data-testid="action-buttons">
          {!partnerPicked && (
            <Button className="w-full h-12 btn-primary-glow" onClick={confirmPickup} disabled={busy} data-testid="btn-confirm-pickup">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-2" />}
              Confirm pickup
            </Button>
          )}
          {partnerPicked && !isTransit && (
            <Button className="w-full h-12 btn-primary-glow" onClick={startTransit} disabled={busy} data-testid="btn-start-transit">
              <Navigation className="w-4 h-4 mr-2" /> Start delivery
            </Button>
          )}
          {(partnerPicked || isTransit) && (
            <Button className="w-full h-12 bg-accent hover:bg-accent text-accent-foreground" onClick={() => setOtpOpen(true)} disabled={busy} data-testid="btn-open-otp">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Delivered — verify OTP
            </Button>
          )}
          <Button variant="outline" className="w-full h-11 text-destructive hover:text-destructive hover:bg-destructive/5 border-destructive/20" onClick={() => setFailOpen(true)} disabled={busy} data-testid="btn-open-fail">
            <XCircle className="w-4 h-4 mr-2" /> Mark failed
          </Button>
        </div>
      )}

      {isDelivered && (
        <Card className="card-soft border-transparent bg-accent/5 border-l-4 border-l-accent" data-testid="delivered-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-accent shrink-0" />
            <div>
              <div className="font-medium">Delivered</div>
              <div className="text-xs text-muted-foreground">Delivered at {fmtDate(order.delivery?.delivered_at)}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card className="card-soft border-transparent" data-testid="delivery-timeline-card">
        <CardContent className="p-4">
          <div className="font-display text-base mb-2">Timeline</div>
          <ol className="space-y-2 text-sm">
            {(order.timeline || []).slice().reverse().map((t, i) => (
              <li key={i} className="flex gap-2 items-start">
                <div className="w-1.5 h-1.5 mt-2 rounded-full bg-accent shrink-0" />
                <div>
                  <div className="capitalize font-medium">{(t.status || "").replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(t.at)}</div>
                  {t.note && <div className="text-[11px] text-muted-foreground italic">{t.note}</div>}
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* OTP dialog */}
      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent data-testid="otp-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-accent" />Verify delivery OTP</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm text-muted-foreground mb-3">Ask the customer for the 4–6 digit OTP they received. Delivery will be marked complete only after verification.</p>
            <Label>Delivery OTP</Label>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              placeholder="●●●●"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="text-center text-3xl font-mono tracking-[0.4em] h-14 mt-2"
              data-testid="otp-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtpOpen(false)}>Cancel</Button>
            <Button onClick={submitDeliver} disabled={busy || otp.length < 4} className="btn-primary-glow" data-testid="otp-submit">
              Confirm delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Failed dialog */}
      <Dialog open={failOpen} onOpenChange={setFailOpen}>
        <DialogContent data-testid="fail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Failed delivery</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {FAILURE_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setFailReason(r)}
                    className={`text-left rounded-lg border p-3 text-sm transition ${failReason === r ? "border-accent bg-accent/5" : "hover:bg-secondary"}`}
                    data-testid={`fail-reason-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Extra notes (optional)</Label>
              <Textarea rows={2} value={failNote} onChange={(e) => setFailNote(e.target.value)} placeholder="Anything the manager should know…" data-testid="fail-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submitFail} disabled={busy} data-testid="fail-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Report failure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Link to="/delivery/history" className="block text-center text-xs text-muted-foreground pt-2 hover:text-foreground" data-testid="link-history">
        View past deliveries →
      </Link>
    </div>
  );
}
